import { tint } from "polished";
import { FC, Fragment, memo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { IMySchedule, ScheduleSituation } from "../../../api/getMySchedules";
import useTimer from "../../../hooks/useTimer";
import ThemeContext from "../../../themes/context";
import { formatDate } from "../../../utils/date-util/dateUtils";
import { CancelIcon, CheckIcon, InfoIcon, StarIcon, MobileMedicalHelp } from "../../Icons";
import CardButton from "./CardButton";
import CardButtonAnexos from "./CardButtonAnexos";

const CardWrapper = styled.div`
	background: #ffffff;
	box-shadow: 0px 4px 39px 6px rgba(0, 0, 0, 0.07);
	border-radius: 10px;
	margin-left: 16px;
	margin-right: 16px;
	margin-top: 7px;
	margin-bottom: 7px;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: flex-start;
	align-content: center;
	width: 100%;
	max-width: 675px;
`;

const ProfessionalInfo = styled.div`
	width: 100%;
	padding: 7px;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: flex-start;
	align-content: center;
	gap: 7px;
`;

const ProfessionalAvatar = styled.img`
	width: 50px;
	height: 50px;
	border-radius: 50px;
`;

const ProfessionalIndentification = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: flex-start;
	align-content: flex-start;
	font-family: Livvic;
	font-style: normal;
	font-weight: 600;
	font-size: 13px;
	line-height: 150%;
	padding-right: 16px;
`;

const CardFooter = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: space-around;
	align-items: normal;
	align-content: normal;
	padding-top: 14px;
	padding-bottom: 14px;
	border-top: 1px solid #e2e4ea;
`;

interface StatusIndicatorProps {
	color?: string;
	textColor?: string;
}

const StatusIndicator = styled.div<StatusIndicatorProps>`
	background-color: ${props => props.color || props.theme.colors.primary};
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 165.5%;
	padding-left: 8px;
	padding-right: 8px;
	border-radius: 5px;
	color: ${props => props.textColor || "#000000"};
	float: right;
	height: 20px;
`;

const StatusIndicatorWrapper = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row-reverse;
	flex-wrap: nowrap;
	justify-content: space-between;
	align-items: center;
	align-content: stretch;
`;

const CardFooterAnexos = styled.div`
	overflow-x: scroll;
	width: 100%;
	display: flex;
	flex-direction: row;
	padding: 10px;
	box-sizing: border-box;
	gap: 10px;
	&::-webkit-scrollbar {
		display: none;
	}
`;

const EvaluatedIndicatorWrapper = styled.div`
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 150%;
	${props => props.theme.colors.yellowVitat || "#6a4caf"};
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	align-content: normal;
	gap: 5px;
	padding: 10px 0;
`;

const ClinicNotice = styled.div`
	width: 100%;
	box-sizing: border-box;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	align-items: flex-start;
	gap: 7px;
	padding: 14px 16px;
	border-top: 1px solid #e2e4ea;
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 150%;
	color: #7a7a7a;
`;

interface CardMySchedulesProps {
	schedule: IMySchedule;
	handleCancel?: () => void;
}

const CardMySchedules: FC<CardMySchedulesProps> = ({ schedule, handleCancel }) => {
	const { colors } = useContext(ThemeContext);
	const navigate = useNavigate();
	const { showTelemedicine, canCancel } = useTimer(schedule.status, schedule.horaAtendimento);

	const handleInfoClick = () => {
		navigate(
			`/medicos/${schedule.profissional}/agendamentos/${schedule.id}/preview?startDate=${schedule.horaAtendimento}&situation=${schedule.status}`,
		);
	};

	// const handleReschedule = () => {
	// 	navigate(`/medicos/${schedule.profissional}/agendamentos?procedure=${schedule.idProcedimento}`);
	// };

	const handleEvaluateSchedule = () => {
		navigate(`/medicos/${schedule.profissional}/agendamentos/${schedule.id}/review`);
	};

	const getStatusIndicator = (situation: ScheduleSituation) => {
		const situations: Record<ScheduleSituation, { color: string; textColor: string }> = {
			Finalizado: {
				color: colors.backgroundFinalizado,
				textColor: colors.textFinalizado,
			},
			Agendado: {
				color: colors.backgroundAgendado,
				textColor: colors.textAgendado,
			},
			Confirmado: {
				color: colors.backgroundConfirmado,
				textColor: colors.textConfirmado,
			},
			Esperando: {
				color: colors.backgroundEsperando,
				textColor: colors.textEsperando,
			},
			Ncompareceu: {
				color: colors.backgroundNaoCompareceu,
				textColor: colors.textNaoCompareceu,
			},
			Cancelado: {
				color: colors.backgroundCancelado,
				textColor: colors.textCancelado,
			},
			Reagendado: {
				color: colors.backgroundReagendado,
				textColor: colors.textReagendado,
			},
			"Em atendimento": {
				color: colors.backgroundAndamento,
				textColor: colors.textAndamento,
			},
			SalaDisponivel: {
				color: colors.backgroundSalaDisponivel,
				textColor: colors.textSalaDisponivel,
			},
		};

		const telemedicineAvailable = situation === "Agendado" && showTelemedicine && !schedule.fromClinic;
		const { color, textColor } = situations[(telemedicineAvailable && "SalaDisponivel") || situation] || situations["Agendado"];

		return (
			<StatusIndicator color={color} textColor={textColor}>
				{(situation === "Ncompareceu" && "Não Compareceu") || (telemedicineAvailable && "Sala disponível") || situation}
			</StatusIndicator>
		);
	};

	const getCardFooter = (situation: ScheduleSituation) => {
		const color = colors.greenVitat || colors.secondary;
		const scheduled = situation === "Agendado";
		const active = scheduled || situation === "Confirmado" || situation === "Esperando" || situation === "Em atendimento";
		// Agendamentos feitos pela clínica não possuem ID válido para as ações do paciente
		if (active && schedule.fromClinic) {
			return (
				<ClinicNotice>
					<InfoIcon size={16} color="#7a7a7a" />
					<div>
						Agendado diretamente pela clínica.
					</div>
				</ClinicNotice>
			);
		}
		if (active) {
			return (
				<CardFooter>
					<CardButton
						disabled={!canCancel || situation === "Esperando" || situation === "Em atendimento"}
						handleClick={handleCancel}
						icon={
							<CancelIcon
								size={20}
								color={!canCancel || situation === "Esperando" || situation === "Em atendimento" ? tint(0.65, color) : color}
							/>
						}
						label="Cancelar"
					/>
					<CardButton handleClick={handleInfoClick} icon={<InfoIcon size={20} color={color} />} label="Informações" />
					<CardButton
						handleClick={() => {
							window.open("vitat://browser?url=" + schedule.linkTelemedicine + "?theme=vitat&webview=true", "_self");
						}}
						icon={<MobileMedicalHelp size={20} color={showTelemedicine ? color : tint(0.65, color)} />}
						label="Teleconsulta"
						disabled={!showTelemedicine}
					/>
				</CardFooter>
			);
		}
		// else if (situation === "Cancelado") {
		// 	return (
		// 		<CardFooter>
		// 			<CardButton handleClick={handleReschedule} icon={<RescheduleIcon size={20} color={colors.secondary} />} label="Reagendar" />
		// 		</CardFooter>
		// 	);
		// }
		else if (situation === "Finalizado") {
			return (
				<Fragment>
					{schedule.anexos.length > 0 && (
						<CardFooterAnexos>
							{/* TODO: VALIDAR ANEXOS */}
							{schedule.anexos.map((anexo, index) => (
								<CardButtonAnexos key={index} label={"teste.pdf"} />
							))}
						</CardFooterAnexos>
					)}
				</Fragment>
			);
		}
	};

	const getEvaluatedIndicator = (evaluated?: boolean) => {
		if (evaluated) {
			return (
				<EvaluatedIndicatorWrapper>
					<CheckIcon color={colors.yellowVitat || "#6A4CAF"} size={14} />
					Avaliado
				</EvaluatedIndicatorWrapper>
			);
		} else {
			return (
				<EvaluatedIndicatorWrapper onClick={handleEvaluateSchedule}>
					<StarIcon color="#D1D1D1" size={14} />
					Avaliar
				</EvaluatedIndicatorWrapper>
			);
		}
	};

	return (
		<CardWrapper>
			<ProfessionalInfo>
				<ProfessionalAvatar src={schedule.imagem} />
				<ProfessionalIndentification>
					<div>
						{schedule.nomeProfissional} {schedule.especialidade?.description && " - " + schedule.especialidade.description}
					</div>
					<div>{formatDate("dd/MM, HH:mm'h'", schedule.horaAtendimento)}</div>
					<StatusIndicatorWrapper>
						{getStatusIndicator(schedule.status)}
						{schedule.status === "Finalizado" && !schedule.fromClinic && getEvaluatedIndicator(schedule.avaliado)}
					</StatusIndicatorWrapper>
				</ProfessionalIndentification>
			</ProfessionalInfo>
			{getCardFooter(schedule.status)}
		</CardWrapper>
	);
};

export default memo(CardMySchedules);
